import { useEffect, useState } from 'react';
import MESSAGES from '../../../../common/data/constants/messages';
import { IPermission } from '../../../../common/data/dtos/permission';
import Badge from '../../../../components/bootstrap/Badge';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardActions, CardBody, CardHeader, CardLabel, CardTitle } from '../../../../components/bootstrap/Card';
import { MoreActions } from '../../../../components/extras/MoreActions';
import { showSuccess, showWarning } from '../../../../components/extras/Notifications';
import PaginationButtons, { dataPagination, PER_COUNT } from '../../../../components/PaginationButtons';
import permissionService from '../../../../services/permission-service';
import LoadingPage from '../../loading/LoadingPage';
import { PermissionScreenModal } from '../../permission-screen/Common/PermissionScreenModal';
import { PermissionModal } from './PermissionModal';

export const CommonPermissionList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(PER_COUNT['10']);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [isModalScreenOpened, setIsModalScreenOpened] = useState(false);

    const [permissions, setPermissions] = useState<IPermission[]>([]);
    const [selectedPermission, setSelectedPermission] = useState<IPermission | null>(null);

    useEffect(() => {
        fetchPermissions();
    }, []);

    const fetchPermissions = async () => {
        setIsLoading(true);

        const response = await permissionService.getPermissions();

        setIsLoading(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        setPermissions(response.permissions || []);
    };

    const toggleModal = (setter: React.Dispatch<React.SetStateAction<boolean>>, isVisible: boolean) => {
        setter(isVisible);
    };

    const handleAddClick = () => {
        toggleModal(setIsModalOpened, true);
    };

    const onModalClose = () => {
        toggleModal(setIsModalOpened, false);
        setSelectedPermission(null);
    };

    const onScreenModalClose = () => {
        toggleModal(setIsModalScreenOpened, false);
        setSelectedPermission(null);
    };

    const onAfterSubmit = async () => {
        await fetchPermissions();
        onModalClose();
    };

    const onScreenAfterSubmit = async () => {
        await fetchPermissions();
        onScreenModalClose();
    };

    const handleDeleteClick = async (permission: IPermission) => {
        if (!permission.id) {
            showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
            return;
        }

        setIsLoading(true);

        const response = await permissionService.deletePermission(permission.id);

        setIsLoading(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        showSuccess(MESSAGES.ACTION.DELETED);
        await fetchPermissions();
    };

    const handleEditClick = (permission: IPermission) => {
        setSelectedPermission(permission);
        toggleModal(setIsModalOpened, true);
    };

    const handleScreenClick = async (permission: IPermission | null) => {
        setSelectedPermission(permission);
        toggleModal(setIsModalScreenOpened, true);
    };

    if (isLoading) {
        return <LoadingPage />;
    }

    return (
        <>
            <Card style={{ minHeight: 600 }}>
                <CardHeader borderSize={1}>
                    <CardLabel icon="Lock" iconColor="primary">
                        <CardTitle tag="div" className="h5">
                            Permissões
                        </CardTitle>
                    </CardLabel>
                    <CardActions>
                        <Button color="light" icon="AddCircle" onClick={handleAddClick}>
                            Adicionar
                        </Button>
                    </CardActions>
                </CardHeader>
                <CardBody className="table-responsive">
                    <table className="table table-modern table-hover">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Situação</th>
                                <td aria-labelledby="Actions" />
                            </tr>
                        </thead>
                        <tbody>
                            {dataPagination(permissions, currentPage, perPage).map((item: IPermission) => (
                                <tr key={item.id}>
                                    <td>{item.name}</td>
                                    <td>
                                        {item.status ? (
                                            <h5>
                                                <Badge color="success" className="align-items-center justify-content-center">
                                                    Ativo
                                                </Badge>
                                            </h5>
                                        ) : (
                                            <h5>
                                                <Badge color="danger" className="align-items-center justify-content-center">
                                                    Inativo
                                                </Badge>
                                            </h5>
                                        )}
                                    </td>

                                    <MoreActions
                                        id={item.id}
                                        showEdit={true}
                                        showDelete={true}
                                        onEditClick={() => handleEditClick(item)}
                                        onDeleteClick={() => handleDeleteClick(item)}
                                        customActions={[
                                            {
                                                label: 'Telas',
                                                icon: 'ScreenShare',
                                                onClickAction: () => handleScreenClick(item),
                                            },
                                        ]}
                                    />
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardBody>
                <PaginationButtons data={permissions} label="items" setCurrentPage={setCurrentPage} currentPage={currentPage} perPage={perPage} setPerPage={setPerPage} />
            </Card>

            <PermissionModal isModalOpened={isModalOpened} onModalClose={onModalClose} onAfterSubmit={onAfterSubmit} selectedPermission={selectedPermission} />

            <PermissionScreenModal isModalOpened={isModalScreenOpened} onModalClose={onScreenModalClose} onAfterSubmit={onScreenAfterSubmit} selectedPermission={selectedPermission} />
        </>
    );
};
